import {
    BadgeColor,
    BadgeSize,
    ButtonFlavor,
    ButtonType,
    ExBadge,
    ExButton,
    ExEmptyState,
    ExLink,
    ExLoader,
    LinkSize,
} from '@boomi/exosphere';
import { useEffect, useState } from 'react';
import { FABRIC_BASE_URL } from '../config';
import { type Connector, getConnectors } from '../connectors/connectors';
import { GENERIC_ERROR_MESSAGE } from '../shared/apiUtils';
import ErrorAlertBanner from '../shared/ErrorAlertBanner';
import listStyles from '../shared/List.module.css';
import Table, { type TableColumn } from '../shared/Table';
import styles from './AllConnectionsList.module.css';
import ConnectionDrawer from './ConnectionDrawer';
import { type Connection, getAllConnections } from './connections';
import { useConnectionDrawer } from './useConnectionDrawer';

const AllConnectionsList = () => {
    const [connections, setConnections] = useState<Connection[]>([]);
    const [connectors, setConnectors] = useState<Connector[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Refresh the key when refreshKey changes
    useEffect(() => {
        setLoading(true);
        setError(null);
        Promise.all([getAllConnections(), getConnectors()])
            .then(([connectionsData, connectorsData]) => {
                setConnections(connectionsData);
                setConnectors(connectorsData);
            })
            .catch((err) => setError(err instanceof Error ? err.message : GENERIC_ERROR_MESSAGE))
            .finally(() => setLoading(false));
    }, [refreshKey]);

    const connectorMap = new Map(connectors.map((c) => [c.id, c]));

    const {
        drawerMode,
        openCreate,
        openConnection,
        openConnectorView,
        onConnectorSelect,
        navigateToBase,
    } = useConnectionDrawer('connections', connections);

    const columns: TableColumn<Connection>[] = [
        {
            header: 'Config Name',
            width: '40%',
            searchValue: (connection) => connection.name,
            render: (connection) => (
                <ExLink
                    onClick={() => openConnection(connection.appId)}
                    size={LinkSize.SMALL}
                    data-pendo-link="configs-configrow-connectionname"
                    data-pendo-connectorid={connection.connectorId}
                    data-pendo-connectionid={connection.appId}
                >
                    {connection.name}
                </ExLink>
            ),
        },
        {
            header: 'Identity Provider',
            width: '20%',
            searchValue: (connection) => connection.identityProviderName,
            render: (connection) => (
                <ExBadge color={BadgeColor.GRAY} size={BadgeSize.EXTRASMALL}>
                    {connection.identityProviderName}
                </ExBadge>
            ),
            filter: {
                placeholder: 'IDP',
                value: (connection) => connection.identityProviderName,
                order: 2,
            },
        },
        {
            header: 'Connector',
            width: '20%',
            searchValue: (connection) => connectorMap.get(connection.connectorId)?.name ?? '',
            render: (connection) => {
                const connector = connectorMap.get(connection.connectorId);
                return (
                    <div className={styles.connectorCell}>
                        {connector?.icon && (
                            <img src={connector.icon} alt="" width={18} height={18} />
                        )}
                        {connector?.name && (
                            <span className={styles.connectorName}>{connector.name}</span>
                        )}
                    </div>
                );
            },
            filter: {
                placeholder: 'All Connectors',
                showCount: true,
                value: (connection) => connectorMap.get(connection.connectorId)?.name ?? '',
                order: 1,
            },
        },
        {
            header: 'Last Modified',
            width: '120px',
            render: (connection) =>
                new Date(connection.updatedAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                }),
            cellProps: () => ({ className: styles.secondaryText }),
        },
        {
            header: 'Status',
            width: '110px',
            render: (connection) => (
                <ExBadge
                    color={connection.enabled ? BadgeColor.GREEN : BadgeColor.GRAY}
                    size={BadgeSize.EXTRASMALL}
                >
                    {connection.enabled ? 'Enabled' : 'Disabled'}
                </ExBadge>
            ),
            filter: {
                placeholder: 'Status',
                value: (connection) => (connection.enabled ? 'Enabled' : 'Disabled'),
                options: ['Enabled', 'Disabled'],
                order: 3,
            },
        },
    ];

    return (
        <section>
            <div className={listStyles.header}>
                <h2 className={`${listStyles.heading} ex-text-h2`}>Configs</h2>
                <span className={listStyles.pageDescription}>
                    All configurations across all connectors. Each config defines how your
                    connection authenticates.
                </span>
            </div>

            {error ? (
                <ErrorAlertBanner onClose={() => setError(null)}>{error}</ErrorAlertBanner>
            ) : connections.length === 0 ? (
                loading ? (
                    <ExLoader label="Loading configs…" />
                ) : (
                    <ExEmptyState
                        className={listStyles.empty}
                        label="No configs configured"
                        text="Create a config to get started."
                    >
                        <div slot="header">
                            <img
                                src={`${FABRIC_BASE_URL}/emptyStateIcon.svg`}
                                alt="No configs configured"
                            />
                        </div>
                        <ExButton
                            data-pendo-button="configs-emptystate-createconfig"
                            slot="action"
                            type={ButtonType.PRIMARY}
                            flavor={ButtonFlavor.BRANDED}
                            onClick={openCreate}
                        >
                            Create Config
                        </ExButton>
                    </ExEmptyState>
                )
            ) : (
                <div className={listStyles.list}>
                    <Table
                        columns={columns}
                        items={connections}
                        rowKey={(connection) => connection.appId}
                        searchRowClassName={listStyles.search}
                        searchPendoTag="configs-list-search"
                        itemLabel="configs"
                        trailingActions={
                            <ExButton
                                id="configs-createconfig"
                                data-pendo-button="configs-createconfig"
                                type={ButtonType.PRIMARY}
                                flavor={ButtonFlavor.BRANDED}
                                onClick={openCreate}
                            >
                                Create Config
                            </ExButton>
                        }
                    />
                </div>
            )}

            <ConnectionDrawer
                mode={drawerMode}
                onConnectorView={openConnectorView}
                onConnectorSelect={onConnectorSelect}
                onConnectionSelect={openConnection}
                onBack={openCreate}
                onClose={() => {
                    setRefreshKey((k) => k + 1);
                    navigateToBase();
                }}
            />
        </section>
    );
};

export default AllConnectionsList;
